<template>
  <v-container fluid class="login-page pa-0">
    <snack-bar-login :text="notification" />

    <v-dialog max-width="430" v-model="resetPasswordDialog" content-class="recovery-dialog">
      <v-card class="recovery-card">
        <v-form
          v-model="validFormRecovery"
          lazy-validation
          ref="FormRecoveryLogin"
          @submit.prevent="handlePasswordResetEmail"
        >
          <v-card-text class="recovery-card__content">
            <div class="recovery-card__header">
              <h1>Recuperar senha</h1>
              <p>Digite seu e-mail corporativo para receber um link de atualização de senha.</p>
            </div>

            <v-text-field
              required
              :rules="rulesRequiredEmail"
              outlined
              dense
              placeholder="exemplo@exemplo.com"
              v-model="resetPasswordEmail"
            />

            <v-btn
              type="submit"
              depressed
              color="primary"
              :loading="isLoadingRecoveryPassword"
              :disabled="isLoadingRecoveryPassword"
              width="100%"
              class="login-primary-button"
            >
              Enviar
            </v-btn>
          </v-card-text>
        </v-form>
      </v-card>
    </v-dialog>

    <main class="login-shell">
      <section class="login-panel" aria-label="Acesso à plataforma">
        <div class="login-panel__top">
          <img
            src="@/assets/login/waterx-ia-logo-figma-cropped.png"
            alt="WaterX IA"
            class="login-panel__brand"
          />
        </div>

        <v-form
          v-model="validForm"
          lazy-validation
          ref="FormLogin"
          class="login-form"
          @submit.prevent="logIn"
        >
          <div class="login-form__heading">
            <h1>Bem-vindo</h1>
            <p>Acesse sua conta e gerencie sua operação com inteligência.</p>
          </div>

          <label class="field-label" for="email">E-mail corporativo</label>
          <v-text-field
            id="email"
            :rules="rulesRequiredEmail"
            v-model="email"
            placeholder="usuario@fooz.com.br"
            prepend-inner-icon="mail_outline"
            outlined
            dense
            required
            class="login-input"
          />

          <label class="field-label" for="password">Senha</label>
          <v-text-field
            id="password"
            :rules="rulesRequiredPassword"
            :type="isPasswordVisible ? 'text' : 'password'"
            v-model="password"
            placeholder="••••••••••"
            prepend-inner-icon="lock_outline"
            :append-icon="isPasswordVisible ? 'visibility' : 'visibility_off'"
            @click:append="togglePasswordVisibility"
            outlined
            dense
            required
            class="login-input"
          />

          <div class="login-options login-options--forgot-only">
            <button
              type="button"
              class="forgot-password-button"
              @click="resetPasswordDialog = true"
            >
              Esqueci minha senha
            </button>
          </div>

          <v-btn
            type="submit"
            depressed
            :loading="isLoading"
            :disabled="isLoading"
            width="100%"
            class="login-primary-button"
          >
            Entrar na plataforma
            <v-icon right size="18">arrow_forward</v-icon>
          </v-btn>

          <p class="login-terms">
            Ao entrar, você aceita os
            <a
              href="https://fozsustentavel.com.br/terms-of-use/"
              target="_blank"
              rel="noopener noreferrer"
            >Termos</a>
            e a
            <a
              href="https://fozsustentavel.com.br/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
            >Política de Privacidade</a>.
          </p>
        </v-form>

        <footer class="login-panel__footer">
          <!-- logo a ser colocada posteriormente-->
          <!-- <img
            src="@/assets/login/fooz-redefinindo.png"
            alt="FOOZ - Redefinindo o essencial."
            class="login-panel__footer-logo"
          /> -->
          <div class="login-panel__meta">
            <!-- suport / status-->
            <small>2026 FOZ Tecnologia</small>
          </div>
        </footer>
      </section>

      <section class="login-showcase" aria-label="Recursos da plataforma">
        <div class="showcase-copy">
          <h2>
            Recursos<br />
            finitos.<br />
            <span class="showcase-title-blue">Inteligência</span><br />
            <span class="showcase-title-violet">infinita.</span>
          </h2>
          <p>
            Monitore, analise e otimize água, energia e gás com inteligência
            operacional ponta a ponta.
          </p>
        </div>

        <div class="product-preview" aria-hidden="true">
          <div class="laptop">
            <div class="laptop__screen">
              <img src="@/assets/login/login-laptop-screen.png" alt="" />
            </div>
            <div class="laptop__base"></div>
          </div>

          <div class="phone">
            <div class="phone__screen">
              <img src="@/assets/login/login-phone-screen.png" alt="" />
            </div>
          </div>
        </div>

        <div class="showcase-benefits">
          <article>
            <h3>Visão completa</h3>
            <p>Mapa operacional, dashboards e indicadores inteligentes.</p>
          </article>
          <article>
            <h3>Alertas inteligentes</h3>
            <p>Metas, objetivos e monitoramento automatizado.</p>
          </article>
          <article>
            <h3>Eficiência operacional</h3>
            <p>Performance e decisões orientadas por dados.</p>
          </article>
        </div>
      </section>
    </main>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import firebase from "@/firebase";
import router from "@/router";
import SnackBarLogin from "@/pages/Login/SnackBarLogin.vue";
import { RoutesPath } from "@/router/paths.js";
import { MenuFeatures, SubMenuFeatures } from "@/router/features.js";
import { hasMigratedFeature, isMigratedFeature } from "@/router/migratedFeatures.js";
import Rules from "@/utils/Rules";

export default {
  name: "Login",
  components: {
    SnackBarLogin,
  },
  data: () => ({
    validForm: true,
    validFormRecovery: true,
    resetPasswordDialog: false,
    resetPasswordEmail: "",
    isPasswordVisible: false,
    isLoadingRecoveryPassword: false,
    isLoading: false,
    email: "",
    password: "",
    notification: null,
  }),
  async beforeMount() {
    if (this.isLoggedIn) {
      this.redirectRouterPermission(this.getCurrentUserData?.permissions ?? []);
    }
  },
  computed: {
    ...mapGetters(["isLoggedIn", "getCurrentUserData"]),
    rulesRequiredEmail() {
      return Rules.email(true);
    },
    rulesRequiredPassword() {
      return Rules.password();
    },
  },
  methods: {
    ...mapActions(["loginUser"]),
    async logIn() {
      if (this.$refs.FormLogin && !this.$refs.FormLogin.validate()) return;

      this.notification = null;
      this.isLoading = true;
      try {
        await this.loginUser({ email: this.email, password: this.password });
        this.redirectRouterPermission(this.getCurrentUserData.permissions);
      } catch (error) {
        this.handleLoginError(error);
      } finally {
        this.isLoading = false;
      }
    },
    redirectRouterPermission(permissions) {
      // Telas migradas não servem mais de destino pós-login; a ordem abaixo
      // segue o menu "Gestão", que é o que sobrou no legado.
      const landings = [
        { feature: MenuFeatures.PROJECTS, path: RoutesPath.PROJECTS },
        { feature: SubMenuFeatures.PROJECTS_KITS, path: RoutesPath.PROJECTS_KITS },
        { feature: SubMenuFeatures.PROPOSALS, path: RoutesPath.PROPOSALS },
        { feature: MenuFeatures.WATERSCAM, path: RoutesPath.CHOOSE_WATER_SCAN },
        { feature: MenuFeatures.WATERFIX, path: RoutesPath.CHOOSE_WATER_FIX },
        { feature: MenuFeatures.SOLUTIONS, path: RoutesPath.CHOOSE_SOLUTION },
      ];

      const landing = landings.find(
        ({ feature }) => permissions.includes(feature) && !isMigratedFeature(feature)
      );

      if (landing) router.push(landing.path);
      else if (hasMigratedFeature(permissions)) router.push(RoutesPath.MIGRATED);
      else router.push(RoutesPath.FORBIDDEN);
    },
    handleLoginError(error) {
      const errorMessages = {
        "auth/user-not-found": "E-mail e/ou senha inválido",
        "auth/invalid-email": "E-mail e/ou senha inválido",
        "auth/wrong-password": "E-mail e/ou senha inválido",
        "auth/too-many-requests": "Muitas tentativas mal sucedidas, espere um pouco e tente novamente",
        default: "Erro ao entrar, tente novamente mais tarde",
      };
      this.notification = errorMessages[error.message] || errorMessages.default;
    },
    handleResetPasswordError(error) {
      const errorMessages = {
        "auth/user-not-found": "Foi enviado um e-mail para sua caixa de entrada.",
        default: "Erro ao enviar e-mail, tente novamente mais tarde",
      };
      this.notification = errorMessages[error.code] || errorMessages.default;
    },
    async handlePasswordResetEmail() {
      if (this.$refs.FormRecoveryLogin && !this.$refs.FormRecoveryLogin.validate()) return;
      this.isLoadingRecoveryPassword = true;
      this.notification = null;
      try {
        await firebase.auth.sendPasswordResetEmail(this.resetPasswordEmail);
        this.notification = "Foi enviado um e-mail para sua caixa de entrada";
      } catch (e) {
        this.handleResetPasswordError(e);
      } finally {
        this.resetPasswordDialog = false;
        this.isLoadingRecoveryPassword = false;
        this.resetPasswordEmail = "";
      }
    },
    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible;
    },
  },
};
</script>

<style>
@media (max-width: 1180px) and (max-height: 700px) {
  html,
  body,
  #app,
  .v-application,
  .v-application--wrap {
    height: 100vh;
    min-height: 0;
    overflow: hidden !important;
  }
}
</style>

<style scoped>
.login-page {
  min-height: 100vh;
  background: #f1f3f7;
  color: #0e1426;
  font-family: "Inter", "Roboto", Arial, sans-serif;
}

.login-shell {
  display: grid;
  grid-template-columns: minmax(420px, 31.95vw) 1fr;
  min-height: 100vh;
  overflow: hidden;
  background: #fafbfd;
}

.login-panel {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 0;
  border-right: 1px solid #eceff4;
  background: #ffffff;
  padding: 0 clamp(42px, 3.9vw, 74.67px) clamp(32px, 4.4vh, 53px);
}

.login-panel__top {
  display: flex;
  justify-content: flex-start;
  min-height: clamp(210px, 24vh, 286px);
}

.login-panel__brand {
  display: block;
  width: clamp(310px, 25.5vw, 489px);
  max-width: none;
  height: clamp(174px, 22.9vh, 275px);
  margin-top: clamp(-17px, -1.4vh, -8px);
  margin-left: clamp(-41px, -2.1vw, -16px);
  object-fit: contain;
  object-position: left center;
}

.login-form {
  width: 100%;
  max-width: 462.67px;
  margin: clamp(48px, 6vh, 72px) 0 0;
}

.login-form__heading {
  margin-bottom: 26px;
}

.login-form__heading h1 {
  margin: 0 0 9px;
  color: #0e1426;
  font-family: "Geologica", "Inter", "Roboto", Arial, sans-serif;
  font-size: clamp(36px, 2.64vw, 50.7px);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: 0;
}

.login-form__heading p,
.showcase-copy p,
.showcase-benefits p,
.recovery-card__header p {
  margin: 0;
  color: #6b7a93;
  font-size: clamp(16px, 1.04vw, 20px);
  line-height: 1.55;
  letter-spacing: 0;
}

.field-label {
  display: block;
  margin: 18px 0 9px;
  color: #2f3852;
  font-size: clamp(13px, 0.87vw, 16.7px);
  font-weight: 500;
  letter-spacing: 0;
}

.login-input {
  margin-top: 0;
}

.login-input ::v-deep .v-input__slot {
  min-height: 60px !important;
  border-color: rgba(14, 20, 38, 0.1) !important;
  border-radius: 10px !important;
  box-shadow: none !important;
}

.login-input ::v-deep input {
  color: #0e1426;
  font-size: clamp(15px, 1vw, 19.3px);
}

.login-input ::v-deep .v-icon {
  color: #8ea0ba;
}

.login-input ::v-deep .v-input__prepend-inner,
.login-input ::v-deep .v-input__append-inner {
  align-self: stretch;
  align-items: center;
  display: flex;
  margin-top: 0 !important;
}

.login-input ::v-deep .v-input__prepend-inner {
  margin-right: 10px;
}

.login-input ::v-deep .v-input__icon {
  align-items: center;
  display: flex;
}

.login-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 0 20px;
}

.login-options--forgot-only {
  justify-content: flex-end;
  margin-top: 2px;
}

.forgot-password-button {
  flex: 0 0 auto;
  border: 0;
  background: transparent;
  color: #3b86f5;
  cursor: pointer;
  font: inherit;
  font-size: clamp(14px, 0.94vw, 18px);
  font-weight: 500;
  line-height: 1.2;
  padding: 0;
}

.login-primary-button {
  position: relative;
  height: 64px !important;
  border-radius: 10px;
  overflow: hidden;
  background: linear-gradient(90deg, #469ff2 0%, #469ff2 42%, #2f80ed 58%, #0d0eff 78%, #05068f 100%) !important;
  background-position: left center !important;
  background-size: 260% 100% !important;
  box-shadow: 0 6px 14px -8px rgba(47, 128, 237, 0.42) !important;
  color: #ffffff !important;
  font-size: clamp(15px, 1vw, 19.3px);
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  transition: background-position 560ms ease, box-shadow 220ms ease, transform 220ms ease;
}

.login-primary-button ::v-deep .v-btn__content {
  position: relative;
  z-index: 0;
}

.login-primary-button:hover,
.login-primary-button:focus-visible {
  background-position: right center !important;
  box-shadow: 0 8px 16px -10px rgba(47, 128, 237, 0.5) !important;
  transform: translateY(-1px);
}

.login-primary-button:active {
  transform: translateY(0);
}

.login-terms {
  margin: 24px 0 0;
  color: #9aa7bd;
  font-size: clamp(12px, 0.83vw, 16px);
  line-height: 1.5;
}

.login-terms a {
  border-bottom: 1px dashed #c8d2e1;
  color: #6b7a93;
  text-decoration: none;
}

.login-panel__footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-top: auto;
}

.login-panel__footer-logo {
  width: 133px;
  object-fit: contain;
}

.login-panel__meta {
  color: #9aa7bd;
  font-size: clamp(12px, 0.77vw, 14.7px);
  line-height: 1.7;
  text-align: right;
}

.login-panel__meta a {
  color: #9aa7bd;
  margin-left: 14px;
  text-decoration: none;
}

.login-showcase {
  position: relative;
  display: grid;
  grid-template-rows: auto 1fr auto;
  min-height: 0;
  overflow: hidden;
  background:
    radial-gradient(circle at 56% 52%, rgba(0, 176, 251, 0.2) 0, rgba(91, 182, 251, 0.12) 22%, rgba(91, 182, 251, 0.04) 42%, rgba(91, 182, 251, 0) 58%),
    linear-gradient(90deg, rgba(13, 14, 255, 0.04) 1px, transparent 1px),
    linear-gradient(180deg, rgba(13, 14, 255, 0.04) 1px, transparent 1px),
    #fafbfd;
  background-size: auto, 56px 56px, 56px 56px, auto;
  padding: clamp(44px, 6.2vh, 74.67px) clamp(52px, 5vw, 96px) clamp(42px, 8vh, 96px);
}

.showcase-copy {
  position: relative;
  z-index: 2;
  max-width: 330px;
}

.showcase-copy h2 {
  margin: 0 0 12px;
  color: #0e1426;
  font-family: "Geologica", "Inter", "Roboto", Arial, sans-serif;
  font-size: clamp(38px, 2.78vw, 53.3px);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: 0;
}

.showcase-title-blue {
  color: #4d87f4;
}

.showcase-title-violet {
  color: #7067e8;
}

.product-preview {
  position: relative;
  align-self: center;
  justify-self: center;
  width: min(60vw, 720px);
  height: min(32vw, 430px);
  min-height: 310px;
  margin: -16px 0 10px;
}

.product-preview::after {
  position: absolute;
  left: 10%;
  right: 10%;
  bottom: 6%;
  height: 22px;
  border-radius: 999px;
  background: rgba(27, 47, 80, 0.12);
  filter: blur(12px);
  content: "";
}

.laptop {
  position: absolute;
  right: 4%;
  bottom: 11%;
  width: 84%;
  z-index: 2;
}

.laptop__screen {
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border: 6px solid #0e1b2e;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 30px 60px -28px rgba(20, 60, 140, 0.3), 0 12px 24px -12px rgba(20, 60, 140, 0.12);
}

.laptop__screen img,
.phone__screen img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.laptop__base {
  width: 108%;
  height: 13px;
  margin-left: -4%;
  border-radius: 0 0 12px 12px;
  background: linear-gradient(180deg, #d7dee9 0%, #b6c2d4 100%);
}

.laptop__base::after {
  display: block;
  width: 21.6%;
  height: 5px;
  margin: 0 auto;
  border-radius: 0 0 16px 16px;
  background: #8da1b8;
  content: "";
}

.phone {
  position: absolute;
  right: -2%;
  bottom: 8%;
  z-index: 3;
  width: 23%;
  min-width: 104px;
  aspect-ratio: 0.495;
  border: 8px solid #17243a;
  border-radius: 22px;
  background: #ffffff;
  box-shadow: 0 30px 60px -20px rgba(20, 60, 140, 0.35), 0 10px 22px -10px rgba(20, 60, 140, 0.18);
}

.phone__screen {
  height: 100%;
  border-radius: 15px;
  overflow: hidden;
  background: #ffffff;
}

.showcase-benefits {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(34px, 5vw, 90px);
  justify-items: start;
}

.showcase-benefits article {
  justify-self: start;
  min-width: 0;
  text-align: left;
}

.showcase-benefits img {
  display: block;
  width: 58px;
  height: 34px;
  object-fit: contain;
  object-position: left center;
  margin-bottom: 18px;
  margin-left: 0;
  margin-right: auto;
}

.showcase-benefits h3 {
  margin: 0 0 9px;
  color: #0e1426;
  font-size: clamp(15px, 0.97vw, 18.7px);
  font-weight: 700;
  letter-spacing: 0;
}

.showcase-benefits p {
  max-width: 230px;
  font-size: clamp(13px, 0.83vw, 16px);
  line-height: 1.5;
}

.recovery-card {
  border-radius: 18px !important;
}

.recovery-card__content {
  padding: 30px !important;
}

.recovery-card__header {
  margin-bottom: 22px;
}

.recovery-card__header h1 {
  margin: 0 0 8px;
  color: #101d32;
  font-size: 28px;
  line-height: 1.1;
}

@media (max-width: 1180px) {
  .login-shell {
    display: block;
    min-height: 100vh;
    overflow: auto;
    background: #ffffff;
  }

  .login-panel {
    width: min(100%, 613px);
    min-height: 100vh;
    margin: 0 auto;
    border-right: 0;
    padding-inline: clamp(32px, 4vw, 54px);
  }

  .login-showcase {
    display: none;
  }
}

@media (min-width: 901px) and (max-height: 950px) {
  .login-panel {
    padding-bottom: 28px;
  }

  .login-panel__top {
    min-height: 198px;
  }

  .login-panel__brand {
    width: 360px;
    height: 210px;
  }

  .login-form__heading {
    margin-bottom: 18px;
  }

  .login-form__heading h1 {
    font-size: 38px;
  }

  .login-form__heading p {
    font-size: 16px;
    line-height: 1.5;
  }

  .field-label {
    margin: 14px 0 7px;
    font-size: 13px;
  }

  .login-input ::v-deep .v-input__slot {
    min-height: 54px !important;
  }

  .login-input ::v-deep input {
    font-size: 15px;
  }

  .forgot-password-button {
    font-size: 14px;
  }

  .login-primary-button {
    height: 56px !important;
    font-size: 15px;
  }

  .login-terms {
    margin-top: 18px;
    font-size: 12px;
  }

  .login-panel__footer-logo {
    width: 128px;
  }

  .login-panel__meta {
    font-size: 11px;
  }

  .login-showcase {
    padding-top: 46px;
    padding-bottom: 30px;
  }

  .showcase-copy h2 {
    font-size: 38px;
    line-height: 1.08;
  }

  .showcase-copy p {
    font-size: 16px;
    line-height: 1.5;
  }

  .product-preview {
    width: min(58vw, 650px);
    height: 320px;
    min-height: 0;
    margin-top: -4px;
  }

  .showcase-benefits img {
    width: 48px;
    height: 28px;
    margin-bottom: 12px;
  }

  .showcase-benefits h3 {
    font-size: 14px;
  }

  .showcase-benefits p {
    font-size: 12px;
    line-height: 1.35;
  }
}

@media (max-width: 900px) {
  .login-page {
    min-height: 100vh;
  }

  .login-shell {
    display: block;
    min-height: 100vh;
  }

  .login-panel {
    min-height: auto;
    border-right: 0;
    padding: 24px 24px 28px;
  }

  .login-panel__top {
    min-height: 168px;
  }

  .login-panel__brand {
    width: min(420px, 82vw);
    height: 170px;
    margin: -18px 0 0 -22px;
  }

  .login-form {
    max-width: none;
    margin: 38px 0 44px;
  }

  .login-panel__footer {
    align-items: center;
  }

  .login-showcase {
    margin: 0;
    padding: 44px 24px 42px;
  }

  .product-preview {
    width: 100%;
    height: 390px;
    margin: 24px 0 10px;
  }

  .showcase-benefits {
    grid-template-columns: 1fr;
    gap: 28px;
  }
}

@media (max-width: 560px) {
  .login-panel {
    padding-inline: 18px;
  }

  .login-panel__top {
    min-height: 146px;
  }

  .login-panel__brand {
    width: min(360px, 92vw);
    height: 140px;
    margin-left: -18px;
  }

  .login-panel__footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .login-panel__meta {
    text-align: left;
  }

  .login-panel__meta a {
    margin: 0 14px 0 0;
  }

  .login-showcase {
    display: none;
  }
}

@media (max-width: 1180px) and (max-height: 700px) {
  .login-page,
  .login-shell {
    height: 100vh;
    min-height: 0;
    overflow: hidden;
  }

  .login-page {
    position: fixed;
    inset: 0;
    width: 100vw;
   }

  .login-panel {
    display: flex;
    width: min(100%, 540px);
    min-height: 100vh;
    padding: 10px 26px 12px;
  }

  .login-panel__top {
    min-height: 98px;
  }

  .login-panel__brand {
    width: min(300px, 66vw);
    height: 112px;
    margin: -18px 0 0 -16px;
  }

  .login-form {
    margin: 18px 0 14px;
  }

  .login-form__heading {
    margin-bottom: 12px;
  }

  .login-form__heading h1 {
    margin-bottom: 5px;
    font-size: 28px;
    line-height: 1.05;
  }

  .login-form__heading p {
    font-size: 13px;
    line-height: 1.35;
  }

  .field-label {
    margin: 10px 0 5px;
    font-size: 11px;
  }

  .login-input ::v-deep .v-input__slot {
    min-height: 42px !important;
  }

  .login-input ::v-deep input {
    font-size: 13px;
  }

  .login-input ::v-deep .v-input__prepend-inner,
  .login-input ::v-deep .v-input__append-inner {
    min-height: 42px;
  }

  .login-input ::v-deep .v-text-field__details {
    display: none;
  }

  .login-options {
    margin: -2px 0 12px;
  }

  .forgot-password-button {
    font-size: 12px;
  }

  .login-primary-button {
    height: 44px !important;
    font-size: 13px;
  }

  .login-terms {
    margin-top: 11px;
    font-size: 11px;
    line-height: 1.3;
  }

  .login-panel__footer {
    gap: 16px;
  }

  .login-panel__footer-logo {
    width: 96px;
  }

  .login-panel__meta {
    font-size: 10px;
    line-height: 1.35;
  }

  .login-panel__meta a {
    margin-left: 10px;
  }
}

@media (min-width: 901px) and (min-height: 760px) {
  .login-page,
  .login-shell {
    height: 100vh;
    overflow: hidden;
  }
}
</style>
